import { useRouteContext } from '@/app/layouts/TenantLayout'
import { useCommandPaletteStore } from '@/components/command-palette/commandPaletteStore'
import type { PaletteCommand } from '@/components/command-palette/types'
import { announce, stripGoToPrefix } from '@/lib/announcer'
import type { SequenceStarter } from '@/lib/useSequence'
import { useCallback, useMemo } from 'react'

/** Project ids need `adminOnly` admins already get from `getProjectSettingsNav`; orgs have no R or A. */
export const PROJECT_SETTINGS_ID_BY_KEY: Record<string, string> = {
	g: 'general-settings',
	u: 'users-settings',
	r: 'roles-settings',
	k: 'api-keys-settings',
	m: 'metadata-settings',
	a: 'action-settings',
	t: 'streams-settings',
	l: 'audit-logs-settings',
	n: 'anomalies-settings',
	// No clean initial letter was free: M (Metadata) already owns it.
	d: 'modules-settings',
	b: 'subscriber-groups-settings',
	// D is Modules, so Device State takes S for State.
	s: 'device-state-settings',
}

export const ORG_SETTINGS_ID_BY_KEY: Record<string, string> = {
	g: 'org-general-settings',
	u: 'org-users-settings',
	k: 'org-api-keys-settings',
	m: 'org-metadata-settings',
	t: 'org-streams-settings',
	l: 'org-audit-logs-settings',
}

const SETTINGS_CONTINUATIONS = new Set([...Object.keys(PROJECT_SETTINGS_ID_BY_KEY), 'p', 'o'])

/**
 * `S then X`: settings of the current level, plus switching project or organization. The level comes
 * from the route itself (a project URL's own `$tenantId`), not `useNavScope`'s fallback, which would
 * otherwise still point at a project while standing on an organization page.
 */
export function useSettingsStarter(commands: readonly PaletteCommand[]): SequenceStarter {
	const { tenantId: routeTenantId } = useRouteContext()
	const atProject = Boolean(routeTenantId)

	const onComplete = useCallback(
		(key: string) => {
			if (key === 'p') {
				useCommandPaletteStore.getState().openWithView('projects')
				announce('Switch project')
				return
			}
			if (key === 'o') {
				useCommandPaletteStore.getState().openWithView('organizations')
				announce('Switch organization')
				return
			}
			const section = atProject ? 'project-settings' : 'org-settings'
			const navId = (atProject ? PROJECT_SETTINGS_ID_BY_KEY : ORG_SETTINGS_ID_BY_KEY)[key]
			const id = navId && `nav-${section}-${navId}`
			const command = commands.find(c => c.id === id)
			if (!command) {
				return
			}
			command.run()
			announce(stripGoToPrefix(command.title))
		},
		[commands, atProject],
	)

	return useMemo(() => ({ key: 's', continuations: SETTINGS_CONTINUATIONS, onComplete }), [onComplete])
}
